import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, RefreshCw, Shield, AlertCircle, Loader } from 'lucide-react';
import securityApi from '../api/securityApi';

const TrustCenterPublicPage = () => {
  const { slug } = useParams();
  const [trustCenter, setTrustCenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchTrustCenter();
  }, [slug]);

  const fetchTrustCenter = async () => {
    try {
      setLoading(true);
      setNotFound(false);
      const data = await securityApi.getTrustCenterPublic(slug);
      setTrustCenter(data);
    } catch (err) {
      console.error('Error fetching trust center:', err);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-teal-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading trust center...</p>
        </div>
      </div>
    );
  }

  if (notFound || !trustCenter) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Trust Center Not Found</h1>
            <p className="text-gray-600 mb-4">
              The trust center you're looking for doesn't exist or has been unpublished.
            </p>
            <p className="text-sm text-gray-500">
              Please check the URL or contact the company for more information.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-12 text-center">
          <div className="flex items-center justify-center mb-4">
            <Shield className="w-12 h-12 text-teal-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {trustCenter.company_name}
          </h1>
          {trustCenter.company_description && (
            <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
              {trustCenter.company_description}
            </p>
          )}

          {/* Framework Badges and Stats */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {trustCenter.show_framework_badges && trustCenter.framework_badges?.map((badge, i) => (
              <span key={i} className="px-4 py-2 bg-teal-100 text-teal-700 text-sm font-semibold rounded-full border border-teal-200">
                {badge.name} — {badge.status}
              </span>
            ))}
            {trustCenter.total_verified > 0 && (
              <span className="px-4 py-2 bg-green-100 text-green-700 text-sm font-semibold rounded-full border border-green-200 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {trustCenter.total_verified} Verified Controls
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Body Section */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {trustCenter.categories && trustCenter.categories.length > 0 ? (
          <div className="space-y-6">
            {trustCenter.categories.map((category) => (
              <div
                key={category.category}
                className="bg-white rounded-lg shadow-md border border-gray-200 p-6"
              >
                {/* Category Header */}
                <div className="mb-4 pb-3 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-gray-900">
                    {category.category_label}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {category.controls.length} {category.controls.length === 1 ? 'control' : 'controls'} verified
                  </p>
                </div>

                {/* Controls List */}
                <div className="space-y-4">
                  {category.controls.map((control, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-medium text-gray-900">
                          {control.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-2">
                          {control.verification_method === 'Continuously monitored' ? (
                            <>
                              <RefreshCw className="w-4 h-4 text-blue-500" />
                              <span className="text-sm text-blue-600 font-medium">
                                Continuously monitored
                              </span>
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              <span className="text-sm text-green-600 font-medium">
                                {control.verification_method}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-12 text-center">
            <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              No Verified Controls Yet
            </h2>
            <p className="text-gray-600">
              This organization is in the process of completing their compliance verification.
            </p>
          </div>
        )}
      </div>

      {/* Footer Section */}
      <div className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-start gap-3 text-sm text-gray-600">
            <AlertCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="leading-relaxed">
              This trust page only displays security controls that have been independently verified
              through automated integrations or documented evidence. Controls not shown are either
              in progress or pending verification.
              {trustCenter.last_updated_at && (
                <span>
                  {' '}Last updated:{' '}
                  {new Date(trustCenter.last_updated_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                  .
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Powered by footer */}
      <div className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4 text-center">
          <p className="text-xs text-gray-500">
            Secured with Orbis AI Security
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrustCenterPublicPage;
